import React, { useEffect, useMemo, useState } from 'react';

import Checker from './Checker';

import Landing, { shouldSkipLanding } from './Landing';
import TopNav from './TopNav';

const ELIGIBILITY_KEY = 'polyrth.eligibility.v1';
const CHECKER_NOTICE_KEY = 'polyrth.checker.notice.v1';

function getStoredEligibility() {
  try {
    const raw = sessionStorage.getItem(ELIGIBILITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const wallet = String(parsed?.wallet || '').trim().toLowerCase();
    const eligible = Boolean(parsed?.eligible);
    const phase = parsed?.phase != null ? String(parsed.phase) : null;
    if (!wallet || !eligible) return null;
    return { wallet, eligible, phase };
  } catch {
    return null;
  }
}

export default function App() {
  const initialRoute = useMemo(() => {
    if (typeof window === 'undefined') return 'insiders';
    const params = new URLSearchParams(window.location.search);
    const force = params.get('landing');
    if (force === '1' || force === 'true') return 'home';
    // Only auto-open Insiders if eligibility is already known in this session.
    return shouldSkipLanding() && getStoredEligibility() ? 'insiders' : 'home';
  }, []);

  const [route, setRoute] = useState(initialRoute);
  const [insidersAllowed, setInsidersAllowed] = useState(false);

  useEffect(() => {
    // Allow "Esc" to go back to landing while testing/demo
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setRoute('home');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Guard: Insiders only after eligibility check
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (route !== 'insiders') {
        setInsidersAllowed(false);
        return;
      }

      const stored = getStoredEligibility();
      if (!stored?.wallet) {
        try {
          sessionStorage.setItem(
            CHECKER_NOTICE_KEY,
            'Insiders is gated. Check your wallet status first.'
          );
        } catch {
          // ignore
        }
        setRoute('checker');
        return;
      }

      // Verify server-side to prevent client tampering
      try {
        const res = await fetch('/api/check-wallet', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ wallet: stored.wallet }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.eligible) {
          try {
            sessionStorage.setItem(
              CHECKER_NOTICE_KEY,
              'This wallet is not eligible for Insiders.'
            );
          } catch {
            // ignore
          }
          setRoute('checker');
          return;
        }
      } catch {
        if (cancelled) return;
        try {
          sessionStorage.setItem(
            CHECKER_NOTICE_KEY,
            'Unable to verify eligibility right now. Please try again.'
          );
        } catch {
          // ignore
        }
        setRoute('checker');
        return;
      }

      if (cancelled) return;
      setInsidersAllowed(true);
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [route]);

  return (
    <div className="min-h-screen w-full bg-black">
      <TopNav active={route} onNavigate={setRoute} />
      {route === 'home' ? <Landing onEnter={() => setRoute('checker')} /> : null}
      {route === 'insiders' ? (
        <div className="h-screen overflow-hidden">
          {insidersAllowed ? (
            <DesktopGate>
              <PolyrthApp />
            </DesktopGate>
          ) : null}
        </div>
      ) : null}
      {route === 'checker' ? <Checker /> : null}
    </div>
  );
}

